import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { api } from "../api/client";
import { Modal } from "./Modal";

interface MoveCardDialogProps {
  cardId: string;
  currentBoardId: string;
  currentListId: string;
  onClose: () => void;
}

/** Move a card to another list — on this board or any other board. */
export function MoveCardDialog({
  cardId,
  currentBoardId,
  currentListId,
  onClose,
}: MoveCardDialogProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const boardsQ = useQuery({
    queryKey: ["boards"],
    queryFn: () => api.boards.list(),
  });
  const [boardId, setBoardId] = useState(currentBoardId);
  const listsQ = useQuery({
    queryKey: ["board-lists", boardId],
    queryFn: () => api.lists.list(boardId),
    enabled: !!boardId,
  });
  const [listId, setListId] = useState(currentListId);

  // when the board changes, default to its first list
  useEffect(() => {
    const lists = listsQ.data;
    if (!lists) return;
    if (!lists.some((l) => l.id === listId)) setListId(lists[0]?.id ?? "");
  }, [listsQ.data, listId]);

  const move = useMutation({
    // position omitted → card lands at the end of the target list
    mutationFn: () => api.cards.move(cardId, { listId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["card", cardId] });
      qc.invalidateQueries({ queryKey: ["board", currentBoardId] });
      qc.invalidateQueries({ queryKey: ["board", boardId] });
      onClose();
      if (boardId !== currentBoardId)
        navigate(`/boards/${boardId}/cards/${cardId}`);
    },
  });

  const crossBoard = boardId !== currentBoardId;
  const canMove = !!listId && !(boardId === currentBoardId && listId === currentListId);

  return (
    <Modal onClose={onClose} ariaLabel="Move card">
      <div className="p-5 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Move card</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <label className="flex-1 text-sm">
            <span className="text-slate-600">Board</span>
            <select
              value={boardId}
              onChange={(e) => setBoardId(e.target.value)}
              className="mt-1 block w-full rounded border border-slate-300 bg-[var(--surface)] px-2 py-2 text-sm"
            >
              {(boardsQ.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.title}
                </option>
              ))}
            </select>
          </label>
          <label className="flex-1 text-sm">
            <span className="text-slate-600">List</span>
            <select
              value={listId}
              onChange={(e) => setListId(e.target.value)}
              className="mt-1 block w-full rounded border border-slate-300 bg-[var(--surface)] px-2 py-2 text-sm"
            >
              {(listsQ.data ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.title}
                </option>
              ))}
              {(listsQ.data ?? []).length === 0 && (
                <option value="">No lists on this board</option>
              )}
            </select>
          </label>
        </div>

        {crossBoard && (
          <p className="mt-3 text-xs text-slate-500">
            Moving to another board removes the card's labels, assignees, and
            card relationships — those belong to this board.
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => move.mutate()}
            disabled={!canMove || move.isPending}
            className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {move.isPending ? "Moving…" : "Move"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
